import React, { ComponentProps, ReactElement } from "react";
import { FontStyles } from "../../utils/font-styles.ts";
import { State } from "./input-states.ts";
import "../../../assets/fa-icons/css/fontawesome.css";
import "../../../assets/fa-icons/css/sharp-regular.css";

export type InputProps = ComponentProps<"input"> & {
  state: State;
  label?: string;
  message?: string;
  children?: React.ReactNode;
  renderLeadingIcon?: () => ReactElement;
};

// Base styles
const labelStyle = "text-slate font-semibold " + FontStyles["body-md"];
const baseInputStyle = "border rounded font-normal p-4 xs:p-3.5 w-full " + FontStyles["body-l"];
const baseMessageStyle = "font-normal " + FontStyles["body-s"];

// Input variant styles
const defaultStyle =
  "text-slate placeholder:text-light-slate border-foggy-firth hover:border-neutral-300 focus:shadow-[0_0_0_4px] focus:outline-north-sea focus:shadow-north-sea-50";
const successStyle =
  "text-slate placeholder:text-light-slate border-glen shadow-[0_0_0_4px] focus:shadow-[0_0_0_4px] focus:outline-glen shadow-glen-50 focus:shadow-glen-50";
const errorStyle =
  "text-slate placeholder:text-light-slate border-brick shadow-[0_0_0_4px] focus:shadow-[0_0_0_4px] focus:outline-brick shadow-brick-50 focus:shadow-brick-50";
const disabledStyle = "text-neutral-200 placeholder:text-neutral-200 bg-cloud cursor-not-allowed border-foggy-firth";

// Message variant styles
const defaultMessage = "text-light-slate";
const successMessage = "text-glen";
const errorMessage = "text-brick";
const disabledMessage = "text-neutral-200";

// Icon style
const iconContainer = "absolute inset-y-0 pr-0.5 flex items-center";

export const Input: React.FC<InputProps> = ({
  state,
  id,
  placeholder,
  label,
  message,
  type,
  children,
  value,
  onChange,
  onBlur,
  renderLeadingIcon,
}) => {
  const inputStyle: string[] = [baseInputStyle];
  const messageStyle: string[] = [baseMessageStyle];

  switch (state) {
    case "default":
      inputStyle.push(defaultStyle);
      messageStyle.push(defaultMessage);
      break;
    case "success":
      inputStyle.push(successStyle);
      messageStyle.push(successMessage);
      break;
    case "error":
      inputStyle.push(errorStyle);
      messageStyle.push(errorMessage);
      break;
    case "disabled":
      inputStyle.push(disabledStyle);
      messageStyle.push(disabledMessage);
  }
  const leadingIcon = renderLeadingIcon ? renderLeadingIcon() : null;

  if (leadingIcon) {
    inputStyle.push("pl-10 xs:pl-10");
    children = <div className={iconContainer}>{leadingIcon}</div>;
  }

  return (
    <div className="flex flex-col w-full gap-1.5 md:gap-2">
      {label && (
        <label className={labelStyle} htmlFor={id}>
          {label}
        </label>
      )}
      <div className="relative w-full">
        <input
          className={inputStyle.join(" ")}
          id={id}
          type={type}
          placeholder={placeholder}
          disabled={state === "disabled"}
          aria-invalid={state === "error"}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
        />
        {children}
      </div>
      {message && <span className={messageStyle.join(" ")}>{message}</span>}
    </div>
  );
};
